import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, useFormikContext } from 'formik';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import { Button, Form } from 'react-bootstrap';

import Placeholder from 'components/Placeholder';
import FA from 'utils/FA';
import {
  fields as reviewDecisionDuck
} from 'ducks';
import { ensureKeyed } from 'utils';
import { duckify, singleDuck } from '../../../utils/duckify';
import ReviewPage from './ReviewPage';
import './styles/DecisionTypes.scss';

const DecisionList = () => {
  const { values, isSubmitting, setFieldValue } = useFormikContext();

  return (
    <div className='review-sub-section-decision review-sub-section'>
      <Form.Group className='review-setup-decision'>
        <Form.Label className='review-setup-decision-label'>
          Review Decision
        </Form.Label>
        <div className='review-setup-decision-types'>
          {values.reviewDecisions.options && values.reviewDecisions.options.map((option, index) => (
            <div className='review-setup-decision-type'>
              <div className='review-setup-decision-type-input'>
                <Field
                  as={Form.Control}
                  size='sm'
                  name={`reviewDecisions.options.${index}`}
                  placeholder='Ex. "Accept", "Waitlist", or "Decline"'
                  disabled={isSubmitting}
                />
              </div>
              {(values.reviewDecisions.options.length > 1 && !isSubmitting) && (
                <div className='review-settings-item-remove'>
                  <Button
                    variant='link'
                    className='review-settings-item-remove-button'
                    onClick={() => {
                      const array = [...values.reviewDecisions.options];
                      array.splice(index, 1);
                      setFieldValue('reviewDecisions.options', array);
                    }}
                    disabled={isSubmitting}
                  >
                    <FA icon={faTimesCircle} nbsp />
                  </Button>
                </div>
              )}
            </div>
          )).map(ensureKeyed('decision_item'))}
          <Button
            variant='link'
            className='review-settings-add-field'
            onClick={() => setFieldValue(
              'reviewDecisions.options',
              [...values.reviewDecisions.options, '']
            )}
            disabled={isSubmitting}
          >
            <FA icon={faPlusCircle} nbsp />
            Add another
          </Button>
        </div>
      </Form.Group>
    </div>
  );
};

const DecisionTypes = ({
  className,
  reviewDecisions,
  route,
  allDucksReady,
  saveReviewDecisions,
  reloadDucks
}) => {
  const [formikValues, setFormikValues] = useState({ reviewDecisions });

  /** Initialize Formik Values */
  useEffect(() => {
    if (allDucksReady) {
      setFormikValues({ reviewDecisions });
    }
  }, [allDucksReady]);

  const onSubmit = ({ values }) => saveReviewDecisions(values.reviewDecisions);

  return (
    <ReviewPage
      className={className}
      header='Review Decision Types'
      headerHelperText='Define the options for your Review Decision field.'
      initialFormikValues={formikValues}
      route={route}
      onSubmit={onSubmit}
      reloadDucks={reloadDucks}
    >
      <Placeholder.loading
        ready={allDucksReady}
        content={() => <DecisionList />}
      />
    </ReviewPage>
  );
};

DecisionTypes.propTypes = {
  /** Classname prop */
  className: PropTypes.string,

  /** Duck that provides review decisions */
  reviewDecisions: PropTypes.shape({
    id: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,

  /** Current React Router route object */
  route: PropTypes.shape({}).isRequired,

  /** AllDucksReady boolean */
  allDucksReady: PropTypes.bool.isRequired,

  /** Submit Ducks Function */
  saveReviewDecisions: PropTypes.func.isRequired,

  /** marks ducks dirty for reload */
  reloadDucks: PropTypes.func.isRequired
};

DecisionTypes.defaultProps = {
  className: ''
};

const mapDucksToProps = () => ({
  reviewDecisions: singleDuck(reviewDecisionDuck, 'review_decision')
});

const mapDispatchToProps = dispatch => ({
  saveReviewDecisions: reviewDecisions => (
    dispatch(reviewDecisionDuck.actions.update(
      reviewDecisions
    )))
    .then(reviewDecisionDuck.actions.denormalizeObjects),
  reloadDucks: () => dispatch(reviewDecisionDuck.actions.markDirty())
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(DecisionTypes);
